import React from 'react'
import {
  useToast,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  Button
} from '@chakra-ui/react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import { useStoreActions } from 'easy-peasy'
import { isMobile } from 'react-device-detect'
import { ArrowDownIcon } from '@blueprinthq/joy'
import { SubMenuItem } from '../../../components'
import { usePdfDownloader } from '../../../hooks'
import config from '../../../config'
import { useExperienceManager } from '@hooks'
import { usePatientControllerDeleteClient } from '~/clinician-api'
import { DeleteConfirmation } from '@components'

export default function PatientPreferenceMenuBase({
  patient,
  anchorEl,
  handleMenuClose,
  disablePDF
}) {
  const toast = useToast()
  const history = useHistory()

  const {
    isOpen: isDeleteConfirmationOpen,
    onOpen: openDeleteConfirmation,
    onClose: closeDeleteConfirmation
  } = useDisclosure()

  const openArchivePatientModal = useStoreActions(
    actions => actions.modals.archivePatient.openModal
  )
  const openTransferPatientModal = useStoreActions(
    actions => actions.modals.transferPatient.openModal
  )
  const openAssignAdditionalModal = useStoreActions(
    actions => actions.modals.assignAdditionalClinician.openModal
  )
  const setSnackbarMessage = useStoreActions(
    actions => actions.snackbar.setMessage
  )

  const { isEvidenceBasedCareEnabled } = useExperienceManager()

  const {
    mutateAsync: deleteClient,
    isLoading: isDeleting
  } = usePatientControllerDeleteClient()

  const { download: downloadLatestAssessment } = usePdfDownloader({
    url: `${process.env.REACT_APP_NODE_API_ROOT_URL}/clinicians/clients/${patient.id}/reports/latest-assessment`,
    pdfName: `${patient.first_name}_${patient.last_name}_${moment().format(
      'MM/D/YYYY'
    )} - ${config.appName} Latest Assessments Report`
  })

  const { download: downloadSummary } = usePdfDownloader({
    url: `${process.env.REACT_APP_NODE_API_ROOT_URL}/clinicians/clients/${patient.id}/reports/summary`,
    pdfName: `${patient.first_name}_${patient.last_name}_${moment().format(
      'MM/D/YYYY'
    )} - ${config.appName} Summary Report`
  })

  const { download: downloadFullReport } = usePdfDownloader({
    url: `${process.env.REACT_APP_NODE_API_ROOT_URL}/clinicians/clients/${patient.id}/reports/full-report`,
    pdfName: `${patient.first_name}_${patient.last_name}_${moment().format(
      'MM/D/YYYY'
    )} - ${config.appName} Full Report`
  })

  const menuItems = [
    {
      key: 'summary',
      onClick: async () => {
        handleMenuClose()
        setSnackbarMessage({
          variant: 'info',
          message: 'Downloading...',
          duration: 10000
        })
        await downloadSummary()
        setSnackbarMessage({
          variant: 'success',
          message: 'Finished Download!',
          duration: 3000
        })
      },
      display: 'Summary'
    },
    {
      key: 'latest',
      onClick: async () => {
        handleMenuClose()
        setSnackbarMessage({
          variant: 'info',
          message: 'Downloading...',
          duration: 10000
        })
        await downloadLatestAssessment()
        setSnackbarMessage({
          variant: 'success',
          message: 'Finished Download!',
          duration: 3000
        })
      },
      display: 'Latest assessment(s)'
    },
    {
      key: 'full',
      onClick: async () => {
        handleMenuClose()
        setSnackbarMessage({
          variant: 'info',
          message: 'Downloading...',
          duration: 10000
        })
        await downloadFullReport()
        setSnackbarMessage({
          variant: 'success',
          message: 'Finished Download!',
          duration: 3000
        })
      },
      display: 'Full Report'
    }
  ].filter(item => item !== null)

  const handleDeleteClient = () => {
    deleteClient(
      { patientId: patient.id },
      {
        onSuccess: () => {
          toast({
            title: 'Client deleted',
            status: 'success',
            isClosable: true,
            duration: 3000
          })
          history.replace('/scribe/client-list')
        }
      }
    )
  }

  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<ArrowDownIcon />}
          variant="outline"
          p="8px 12px 8px 24px"
          _focus={{
            outline: 'none'
          }}
          w="130px"
        >
          Actions
        </MenuButton>
        <MenuList>
          <MenuItem
            onClick={() => {
              openTransferPatientModal({ patientId: patient.id })
              handleMenuClose()
            }}
          >
            Transfer Client
          </MenuItem>
          <MenuItem
            onClick={() => {
              openAssignAdditionalModal({ patientId: patient.id })
              handleMenuClose()
            }}
          >
            Manage assigned Clinicians
          </MenuItem>
          {!disablePDF && !isMobile && isEvidenceBasedCareEnabled && (
            <SubMenuItem title="Download PDF report" menuItems={menuItems} />
          )}
          {isEvidenceBasedCareEnabled ? (
            <>
              <Divider />
              <MenuItem
                color={patient.is_archived ? 'inherit' : 'error'}
                onClick={() => {
                  openArchivePatientModal({ patientId: patient.id })
                  handleMenuClose()
                }}
              >
                {patient.is_archived ? 'Unarchive' : 'Discharge Client'}
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem
                color="inherit"
                onClick={() => {
                  openArchivePatientModal({ patientId: patient.id })
                  handleMenuClose()
                }}
              >
                {patient.is_archived ? 'Unarchive' : 'Archive Client'}
              </MenuItem>
              <Divider />
              <MenuItem color="error" onClick={openDeleteConfirmation}>
                Delete client
              </MenuItem>
            </>
          )}
        </MenuList>
      </Menu>
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={closeDeleteConfirmation}
        onConfirm={handleDeleteClient}
        isConfirming={isDeleting}
        header="Delete this client?"
        text="Deleting a client will permanently remove all data associated to the client and you will no longer be able to access their profile."
        confirmText="Delete client"
      />
    </>
  )
}
